import { env } from './env'

/**
 * Per-page metadata for in-app navigation.
 *
 * THE CARD IS BUILT FROM THE SERVER'S HTML, NOT FROM HERE. A crawler fetches
 * the URL fresh and never runs this file; `server.mjs` is what gives it the
 * title, the og/twitter tags and — crucially — a real, visible hero image.
 *
 * What this still earns its place for: `document.title`. WeChat's default card
 * uses the LIVE title, so an SPA that never updates it shares every event
 * under the app name. The og:* tags are kept in sync for the same reason —
 * cheap, and correct if anything reads the live DOM.
 *
 * It no longer plants a hidden <img>. That was a guess, and the wrong one: an
 * `opacity:0` element parked at `top:-9999px` is precisely what a thumbnail
 * ranker exists to reject. The Detourr web app has no such element and its
 * WeChat cards are fine — because its server sends a real rendered image.
 */

/**
 * The fallback card image: the Top Chopsticks mark on the app's background.
 *
 * Lives in `public/`, so it keeps a STABLE path — `/share-default.png` — while
 * everything in src/assets gets a content hash that changes on each build. A
 * share card URL that breaks on deploy is worse than no card.
 *
 * It is 600x600 on purpose. The logo file itself is 620x265, and WeChat crops
 * share thumbnails to a square, which would have sliced the wordmark in half.
 *
 * Absolute, because og:image and WeChat both need a full URL — a crawler
 * resolving `/share-default.png` against nothing gets nothing.
 */
export const shareImageUrl = () =>
  `${env.publicOrigin || window.location.origin}/share-default.png`

const setMetaTag = (selector: string, attribute: 'name' | 'property', key: string, content: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}


export function setPageMeta({
  title, description, image, url
}: { title: string; description?: string; image?: string; url?: string }) {
  const fullTitle = title ? `${title} · 拾箸同席` : '拾箸同席 Top Chopsticks'
  const img = image || shareImageUrl()
  const link = url || window.location.href

  document.title = fullTitle
  setMetaTag('meta[name="description"]', 'name', 'description', description || '')
  setMetaTag('meta[property="og:title"]', 'property', 'og:title', fullTitle)
  setMetaTag('meta[property="og:description"]', 'property', 'og:description', description || '')
  setMetaTag('meta[property="og:image"]', 'property', 'og:image', img)
  setMetaTag('meta[property="og:url"]', 'property', 'og:url', link)
  setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website')
}

/** Back to the app defaults when leaving an event page. */
export function resetPageMeta() {
  setPageMeta({
    title: '',
    description: 'Social dining in Shanghai.',
    image: shareImageUrl(),
    url: env.publicOrigin || window.location.origin
  })
}
